/**
 * The stories behind the Week 3 map toys (`week3MapToys.tsx`), kept free of
 * React so they can be tested: a foggy mountain climbed by gradient ascent, a
 * camera that hunts for focus, and a pyramid whose hidden room shows itself
 * to falling muons. Every length is in the toys' 100-wide viewBox units.
 */

/** A source of uniform random numbers in [0, 1), like `Math.random`. */
export type Random = () => number;

/** One piece of a mountain. */
export interface HillBump {
  /** Where the bump is centered. */
  center: number;
  /** How tall the bump is at its center. */
  height: number;
  /** How far the bump reaches either side of its center. */
  spread: number;
  /**
   * A plateau is flat on top with steep sides; otherwise the bump is a
   * rounded Gaussian peak. Omitted means a peak.
   */
  isPlateau?: boolean;
}

/** A mountain made of bumps, and how far each climbing step goes. */
export interface Hill {
  bumps: readonly HillBump[];
  /** The step size: each step moves `stepSize × slope`, up to {@link MAX_STEP}. */
  stepSize: number;
}

/** The longest stride a step may take. */
export const MAX_STEP = 11;

/** The stretch of ground the hill is drawn and climbed on. */
export const HILL_SPAN = {left: 6, right: 94} as const;

/** A step shorter than this is too small to see, so the climber is at a top. */
const TOP_STEP = 0.6;

/** Climbs end at the top after this many steps, however close they got. */
export const MAX_CLIMB_STEPS = 14;

/** The shortest first step a new climb may start with. */
const MIN_FIRST_STEP = 3;

/**
 * Chance that a mountain has a false summit: a knoll on the ledge, a top of
 * its own, where a climb in the fog stops short of the real summit.
 */
export const FALSE_SUMMIT_CHANCE = 0.25;

/** The bump's height and slope at `x`. */
function bumpAt({bump, x}: {bump: HillBump; x: number}): {
  height: number;
  slope: number;
} {
  // A peak is exp(−u²/2), a plateau the flat-topped, steep-walled
  // exp(−u⁸/8), with u the distance from the center in spreads.
  const power = bump.isPlateau ? 8 : 2;
  const u = (x - bump.center) / bump.spread;
  const height = bump.height * Math.exp(-(Math.abs(u) ** power) / power);
  const slope =
    (-height * Math.sign(u) * Math.abs(u) ** (power - 1)) / bump.spread;
  return {height, slope};
}

/**
 * The hill's height at `x`.
 *
 * @param params.hill - The hill.
 * @param params.x - A position on the ground.
 * @return The height, at least 0.
 * @throws Never.
 */
export function hillHeight({hill, x}: {hill: Hill; x: number}): number {
  return hill.bumps.reduce((sum, bump) => sum + bumpAt({bump, x}).height, 0);
}

/**
 * The hill's slope at `x`: how much the height rises per unit to the right.
 *
 * @param params.hill - The hill.
 * @param params.x - A position on the ground.
 * @return The derivative of {@link hillHeight} at `x`.
 * @throws Never.
 */
export function hillSlope({hill, x}: {hill: Hill; x: number}): number {
  return hill.bumps.reduce((sum, bump) => sum + bumpAt({bump, x}).slope, 0);
}

/**
 * Where one gradient ascent step from `x` lands: `x + stepSize × slope`,
 * no longer than {@link MAX_STEP}, kept on the drawn ground, and stopped at
 * any top it would pass.
 *
 * @param params.hill - The hill.
 * @param params.x - Where the climber stands.
 * @return The new position.
 * @throws Never.
 */
export function climbStep({hill, x}: {hill: Hill; x: number}): number {
  const slope = hillSlope({hill, x});
  const stride = Math.min(MAX_STEP, hill.stepSize * Math.abs(slope));
  const direction = Math.sign(slope);
  const target = Math.min(
    HILL_SPAN.right,
    Math.max(HILL_SPAN.left, x + direction * stride),
  );
  // The step size is large so plateaus and walls go quickly, which would
  // carry the climber past a top; a step that crosses one stops on it.
  const distance = Math.abs(target - x);
  let reached = 0;
  while (reached < distance) {
    const next = Math.min(distance, reached + 0.25);
    if (hillSlope({hill, x: x + direction * next}) * direction <= 0) {
      let low = reached;
      let high = next;
      for (let round = 0; round < 30; round += 1) {
        const middle = (low + high) / 2;
        if (hillSlope({hill, x: x + direction * middle}) * direction > 0) {
          low = middle;
        } else {
          high = middle;
        }
      }
      return x + direction * low;
    }
    reached = next;
  }
  return target;
}

/**
 * Whether the climber at `x` has reached a top: the next step would be too
 * small to see.
 *
 * @param params.hill - The hill.
 * @param params.x - Where the climber stands.
 * @return True at a top.
 * @throws Never.
 */
export function isAtTop({hill, x}: {hill: Hill; x: number}): boolean {
  return Math.abs(climbStep({hill, x}) - x) < TOP_STEP;
}

/**
 * The top a climb from `x` ends on, found by stepping until the steps
 * vanish.
 *
 * @param params.hill - The hill.
 * @param params.x - Where the climb starts.
 * @return The position of that top.
 * @throws Never.
 */
export function topFrom({hill, x}: {hill: Hill; x: number}): number {
  let position = x;
  for (let step = 0; step < 2000; step += 1) {
    const next = climbStep({hill, x: position});
    if (Math.abs(next - position) < 1e-4) {
      return next;
    }
    position = next;
  }
  return position;
}

/**
 * Every top of the hill, left to right: each peak's summit, found by
 * climbing from its center, with duplicates merged.
 *
 * @param hill - The hill.
 * @return The positions of the tops.
 * @throws Never.
 */
export function hillTops(hill: Hill): number[] {
  const tops: number[] = [];
  for (const bump of hill.bumps) {
    const top = topFrom({hill, x: bump.center});
    if (tops.every(other => Math.abs(other - top) > 1)) {
      tops.push(top);
    }
  }
  return tops.sort((a, b) => a - b);
}

/**
 * The position of the hill's highest point.
 *
 * @param hill - The hill.
 * @return The position of the highest top.
 * @throws Never.
 */
export function highestTop(hill: Hill): number {
  return hillTops(hill).reduce((best, top) =>
    hillHeight({hill, x: top}) > hillHeight({hill, x: best}) ? top : best,
  );
}

/**
 * Whether the top at `x` is the hill's highest.
 *
 * @param params.hill - The hill.
 * @param params.x - A top, from {@link topFrom}.
 * @return True when no other top is more than a hair higher.
 * @throws Never.
 */
export function isHighestTop({hill, x}: {hill: Hill; x: number}): boolean {
  return hillHeight({hill, x: highestTop(hill)}) - hillHeight({hill, x}) < 0.5;
}

/** A uniform draw in `[low, high)`. */
function between({
  random,
  low,
  high,
}: {
  random: Random;
  low: number;
  high: number;
}): number {
  return low + random() * (high - low);
}

/**
 * Draws a fresh mountain and a spot at its foot to start climbing from.
 * Every mountain has a wide, gentle base, so the ground always rises
 * toward a top; a snowy peak; and on the climbing side a flat ledge with a
 * steep wall, where the steps shrink to a crawl. Some mountains (see
 * {@link FALSE_SUMMIT_CHANCE}) have a knoll on the ledge, a false summit
 * that ends the climb short of the real one.
 *
 * @param random - Uniform random numbers in [0, 1).
 * @return The hill and the starting position.
 * @throws Never.
 */
export function makeHill(random: Random): {hill: Hill; start: number} {
  const side = random() < 0.5 ? -1 : 1;
  // The peak leans away from the climbing side to leave room for the ledge.
  const peakCenter = 50 - side * between({random, low: 4, high: 8});
  const peak: HillBump = {
    center: peakCenter,
    height: between({random, low: 22, high: 26}),
    spread: between({random, low: 6.5, high: 8}),
  };
  const base: HillBump = {
    center: peakCenter,
    height: between({random, low: 8, high: 10}),
    spread: between({random, low: 26, high: 30}),
  };
  // Flat on top from under the peak out to a steep wall on the climbing
  // side; its far wall is hidden under the far side of the peak.
  const ledge: HillBump = {
    center: peakCenter + side * between({random, low: 5, high: 7}),
    height: between({random, low: 9, high: 12}),
    spread: between({random, low: 19, high: 22}),
    isPlateau: true,
  };
  const bumps: HillBump[] = [base, peak, ledge];
  if (random() < FALSE_SUMMIT_CHANCE) {
    // Tall and narrow enough that the ground dips again before the peak.
    bumps.push({
      center: peakCenter + side * between({random, low: 17, high: 20}),
      height: between({random, low: 6, high: 8}),
      spread: between({random, low: 2.4, high: 3}),
    });
  }
  const hill = {bumps, stepSize: between({random, low: 6.5, high: 8})};

  // Walk in from the ledge side's edge to the first spot steep enough for
  // a visible first step.
  const edge = side < 0 ? HILL_SPAN.left + 1 : HILL_SPAN.right - 1;
  for (let offset = 0; offset < 40; offset += 0.5) {
    const start = edge - side * offset;
    if (Math.abs(climbStep({hill, x: start}) - start) >= MIN_FIRST_STEP) {
      return {hill, start};
    }
  }
  return {hill, start: edge - side * 10};
}

/**
 * How hard the camera's focus ring turns toward sharp each step. Above 1 it
 * overshoots, so the focus hunts back and forth, landing closer each time,
 * as a real autofocus does.
 */
export const FOCUS_GAIN = 1.4;

/** Focus errors smaller than this look sharp. */
export const SHARP_ERROR = 0.08;

/**
 * One autofocus step. Sharpness is `1 − (focus − best)²`, so its slope is
 * `−2 (focus − best)`; a gradient ascent step of size `FOCUS_GAIN / 2`
 * moves the focus by `FOCUS_GAIN × (best − focus)`.
 *
 * @param params.focus - The current focus setting.
 * @param params.best - The setting where the picture is sharp.
 * @return The next focus setting; it snaps to `best` once within
 *     {@link SHARP_ERROR}.
 * @throws Never.
 */
export function focusStep({
  focus,
  best,
}: {
  focus: number;
  best: number;
}): number {
  const next = focus + FOCUS_GAIN * (best - focus);
  return Math.abs(next - best) < SHARP_ERROR ? best : next;
}

/**
 * Draws a blurry starting focus for a new picture, off either way by an
 * amount in `[0.4, 1.3)`, so finding focus takes two to four steps.
 *
 * @param random - Uniform random numbers in [0, 1).
 * @return The focus error to start from (focus minus best).
 * @throws Never.
 */
export function makeFocusError(random: Random): number {
  const size = between({random, low: 0.4, high: 1.3});
  return random() < 0.5 ? -size : size;
}

/** The hidden room inside the pyramid, in the toy's viewBox units. */
export interface Chamber {
  /** Left edge. */
  x: number;
  /** Top edge. */
  y: number;
  width: number;
  height: number;
}

/** The pyramid's outline: apex and the two base corners. */
export const PYRAMID = {
  apex: {x: 50, y: 14},
  left: {x: 8, y: 74},
  right: {x: 92, y: 74},
} as const;

/** Presses of muons it takes for the room to show itself fully. */
export const PRESSES_TO_FIND_ROOM = 3;

/** Muons that fall through the pyramid on each press. */
export const MUONS_PER_PRESS = 9;

/**
 * Draws a hidden room somewhere comfortably inside the pyramid.
 *
 * @param random - Uniform random numbers in [0, 1).
 * @return The room.
 * @throws Never.
 */
export function makeChamber(random: Random): Chamber {
  const width = 26;
  const height = 16;
  const y = between({random, low: 44, high: 52});
  // The pyramid's half width at the room's top edge, less a margin.
  const halfWidth =
    ((y - PYRAMID.apex.y) / (PYRAMID.left.y - PYRAMID.apex.y)) *
    (PYRAMID.apex.x - PYRAMID.left.x);
  const slack = Math.max(0, halfWidth - width / 2 - 6);
  const centerX = PYRAMID.apex.x + between({random, low: -slack, high: slack});
  return {x: centerX - width / 2, y, width, height};
}

/**
 * Where one press's muons fall: half aim at the hidden room, because more
 * muons get through empty space than through stone, and the rest land
 * anywhere across the pyramid.
 *
 * @param params.chamber - The hidden room.
 * @param params.random - Uniform random numbers in [0, 1).
 * @return The horizontal position of each muon, {@link MUONS_PER_PRESS}
 *     of them.
 * @throws Never.
 */
export function muonShower({
  chamber,
  random,
}: {
  chamber: Chamber;
  random: Random;
}): number[] {
  return Array.from({length: MUONS_PER_PRESS}, (_, index) =>
    index % 2 === 0
      ? between({
          random,
          low: chamber.x + 2,
          high: chamber.x + chamber.width - 2,
        })
      : between({
          random,
          low: PYRAMID.left.x + 10,
          high: PYRAMID.right.x - 10,
        }),
  );
}

/** What the hidden room can hold, with draw weights in percent. */
export const CHAMBER_FINDS = [
  {id: 'treasure', name: 'a treasure chest', percent: 30},
  {id: 'cat', name: 'a sleeping cat', percent: 25},
  {id: 'plush-pi', name: 'Plush π', percent: 20},
  {id: 'ollie', name: 'Ollie', percent: 20},
  {id: 'golden-ollie', name: 'a golden Ollie', percent: 5},
] as const;

/** One thing the hidden room can hold. */
export type ChamberFind = (typeof CHAMBER_FINDS)[number];

/**
 * Draws what the room holds, by the weights in {@link CHAMBER_FINDS}.
 *
 * @param random - Uniform random numbers in [0, 1).
 * @return The find.
 * @throws Never.
 */
export function drawChamberFind(random: Random): ChamberFind {
  let ticket = random() * 100;
  for (const find of CHAMBER_FINDS) {
    if (ticket < find.percent) {
      return find;
    }
    ticket -= find.percent;
  }
  return CHAMBER_FINDS[0];
}
